import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronLeft } from 'lucide-react';

const statusColor = { good: '#22C55E', medium: '#F59E0B', poor: '#EF4444' };

function FitToBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 100);
    if (!points || points.length === 0) return;
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    const b = [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
    try { map.fitBounds(b, { padding: [30, 30], maxZoom: 8 }); } catch (e) {}
  }, [points, map]);
  return null;
}

export default function MapPanel({ data, onSelectGovernorate }) {
  const nav = useNavigate();
  const points = data?.points || [];
  const govs = data?.governorates || [];
  const [mode, setMode] = React.useState('markers'); // 'markers' | 'heat'

  const center = useMemo(() => {
    if (points.length === 0) return [19.5, 43.5];
    const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const lng = points.reduce((s, p) => s + p.lng, 0) / points.length;
    return [lat, lng];
  }, [points]);

  return (
    <div className="panel p-4 flex flex-col" style={{ minHeight: 360 }}>
      <div className="panel-header">
        <div>
          <div className="panel-title">خريطة المنطقة الجنوبية</div>
          <div className="panel-subtitle mt-0.5">اضغط على محافظة أو مدرسة للتفاصيل</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="panel px-1 py-0.5 flex text-[10.5px] font-semibold border border-white/5">
            <button onClick={() => setMode('markers')} className={`px-2 py-1 rounded ${mode === 'markers' ? 'bg-blue-600 text-white' : 'text-slate-300'}`}>علامات</button>
            <button onClick={() => setMode('heat')} className={`px-2 py-1 rounded ${mode === 'heat' ? 'bg-blue-600 text-white' : 'text-slate-300'}`}>خريطة حرارية</button>
          </div>
          <span className="pill pill-blue">{points.length.toLocaleString('en-US')} مدرسة</span>
        </div>
      </div>

      <div className="relative rounded-xl overflow-hidden bg-[#0A1120]" style={{ height: 320 }}>
        {points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-500 text-sm">لا توجد إحداثيات مدارس متاحة</div>
        ) : (
          <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%', background: '#0A1120' }} scrollWheelZoom={false}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="se-map-tiles" />
            <FitToBounds points={points} />
            {points.map((p) => {
              // Heat mode: color by open-ticket workload, larger radius, no click
              const heatColor = p.open >= 5 ? '#EF4444' : p.open >= 2 ? '#F59E0B' : '#22C55E';
              const markerColor = statusColor[p.status] || '#22C55E';
              const isHeat = mode === 'heat';
              return (
                <CircleMarker
                  key={p.id}
                  center={[p.lat, p.lng]}
                  radius={isHeat ? Math.min(8 + p.open * 1.5, 24) : Math.min(4 + (p.tickets || 0) * 0.3, 12)}
                  pathOptions={{
                    color: isHeat ? heatColor : markerColor,
                    fillColor: isHeat ? heatColor : markerColor,
                    fillOpacity: isHeat ? 0.35 : 0.7,
                    weight: isHeat ? 0 : 1,
                  }}
                  eventHandlers={{ click: () => p.id && nav(`/school/${encodeURIComponent(p.id)}`) }}
                >
                  <Tooltip direction="top" opacity={0.95}>
                    <div dir="rtl" style={{ fontSize: 11 }}>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div>{p.governorate}</div>
                      <div>البلاغات: {p.tickets} · المفتوحة: {p.open}</div>
                      <div style={{ color: '#93C5FD', marginTop: 2 }}>اضغط لعرض التفاصيل ⟵</div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}

        {/* Legend */}
        <div className="absolute bottom-2 right-3 bg-[#0B1424]/85 border border-white/10 rounded-lg px-3 py-2 text-[11px] space-y-1 z-[1000]">
          {mode === 'heat' ? (
            <>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> <span className="text-slate-300">حمل منخفض</span></div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> <span className="text-slate-300">حمل متوسط</span></div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> <span className="text-slate-300">حمل مرتفع</span></div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> <span className="text-slate-300">جيد</span></div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> <span className="text-slate-300">متوسط</span></div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> <span className="text-slate-300">حرج</span></div>
            </>
          )}
        </div>

        {/* Governorate summary — clickable */}
        {govs.length > 0 && (
          <div className="absolute top-2 left-2 bg-[#0B1424]/90 border border-white/10 rounded-lg text-[11px] z-[1000] max-w-[220px] backdrop-blur-md overflow-hidden">
            <div className="px-3 py-1.5 text-slate-300 font-bold text-[11px] border-b border-white/5 flex items-center justify-between">
              <span>المحافظات</span>
              <span className="text-slate-500 text-[9.5px]">اضغط للتفاصيل</span>
            </div>
            <div className="max-h-52 overflow-y-auto divide-y divide-white/[0.04]">
              {govs.slice(0, 12).map((g) => (
                <button key={g.name}
                  onClick={() => onSelectGovernorate?.(g.name)}
                  data-testid={`map-gov-${g.name}`}
                  className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-white/[0.06] transition-colors text-right">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: statusColor[g.status] }} />
                  <span className="text-slate-200 flex-1 truncate">{g.name}</span>
                  <span className="text-slate-400 tabular-nums">{g.tickets}</span>
                  <ChevronLeft size={11} className="text-slate-500 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
